import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ops`;

export const opsApi = axios.create({ baseURL: API });

opsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('desky_ops_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

opsApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== '/sales/login') {
      localStorage.removeItem('desky_ops_token');
      window.location.href = '/sales/login';
    }
    return Promise.reject(err);
  }
);

export const fmtINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export const fmtINR2 = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n || 0);

export const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(', ');
  return e?.message || 'Something went wrong';
};

// ── Deskey Brand Theme (matches /index.css :root tokens) ──
// Primary brand: vibrant Deskey Green #34A853. Surfaces stay white/very-light.
// Complementary accents: blue (info/secondary) and coral (alerts/critical).
export const GREEN = '#34A853';          // Deskey primary
export const GREEN_DARK = '#2D9249';     // hover
export const GREEN_LIGHT = '#E8F5E9';    // light tint surface
export const GREEN_LIGHTER = '#F0FBF2';  // softest mint
export const BLUE = '#3B82F6';           // complementary info
export const CORAL = '#F05545';          // complementary alert
export const NAVY = '#1A365D';           // Deskey deep navy (used sparingly)
export const INK = '#0F172A';            // primary text
export const MUTED = '#64748B';          // secondary text

// Back-compat aliases for existing component code — all solid, no gradients.
export const GRAD = GREEN;                              // primary action / logo tile
export const GRAD_SOFT = GREEN_LIGHT;                   // soft mint surface
export const SIDEBAR_BG = '#FFFFFF';                    // white sidebar (matches Deskey)
export const FONT_HEAD = { fontFamily: 'Space Grotesk, sans-serif' };

export const STAGES = ['lead', 'contacted', 'demo', 'pilot', 'negotiation', 'active', 'churned'];

export const STAGE_META = {
  lead: { label: 'Lead', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  contacted: { label: 'Contacted', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  demo: { label: 'Demo', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  pilot: { label: 'Pilot', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  negotiation: { label: 'Negotiation', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  active: { label: 'Active', color: 'bg-green-50 text-green-700 border-green-200' },
  churned: { label: 'Churned', color: 'bg-red-50 text-red-600 border-red-200' },
};

export const ROLE_LABEL = {
  super_admin: 'Super Admin',
  zone_admin: 'Zone Admin',
  state_manager: 'State Manager',
  sales_rep: 'Sales Rep',
};

// 4-level hierarchy: sales_rep(1) < state_manager(3) < zone_admin(4) < super_admin(5).
// Must match backend/desk_ops/core.py ROLE_RANK exactly.
export const ROLE_RANK = { sales_rep: 1, state_manager: 3, zone_admin: 4, super_admin: 5 };
// What each role can create immediately below them.
export const ROLE_CREATES = {
  super_admin:   ['zone_admin'],
  zone_admin:    ['state_manager'],
  state_manager: ['sales_rep'],
};
export const INDIA_ZONES = ['North', 'South', 'East', 'West'];

export const StatusPill = ({ value, map }) => {
  const cls = map[value] || 'bg-slate-100 text-slate-600';
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${cls}`}>{String(value || '—').replace(/_/g, ' ')}</span>;
};

export const INVOICE_STATUS = {
  draft: 'bg-slate-100 text-slate-600', sent: 'bg-blue-50 text-blue-700',
  paid: 'bg-green-50 text-green-700', overdue: 'bg-red-50 text-red-600',
  cancelled: 'bg-slate-50 text-slate-400 line-through', refunded: 'bg-purple-50 text-purple-700',
};

export const APPROVAL_STATUS = {
  pending: 'bg-amber-50 text-amber-700', approved: 'bg-green-50 text-green-700', rejected: 'bg-red-50 text-red-600',
};

export const KIT_STATUS = {
  in_stock: 'bg-slate-100 text-slate-600', allocated: 'bg-green-50 text-green-700',
  faulty: 'bg-red-50 text-red-600', retired: 'bg-slate-50 text-slate-400',
};

export const HEALTH_STATUS = {
  healthy: 'bg-green-50 text-green-700', degraded: 'bg-amber-50 text-amber-700', faulty: 'bg-red-50 text-red-600',
};

export const TICKET_STATUS = {
  open: 'bg-red-50 text-red-600', in_progress: 'bg-amber-50 text-amber-700', resolved: 'bg-green-50 text-green-700',
};

export const Pagination = ({ data, onPage }) => {
  if (!data || data.pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-xs text-slate-500" data-testid="pagination">
      <span>{fmtNum(data.total)} records · page {data.page} of {data.pages}</span>
      <div className="flex gap-1.5">
        <button data-testid="page-prev" disabled={data.page <= 1} onClick={() => onPage(data.page - 1)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40 hover:border-slate-300">Prev</button>
        <button data-testid="page-next" disabled={data.page >= data.pages} onClick={() => onPage(data.page + 1)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40 hover:border-slate-300">Next</button>
      </div>
    </div>
  );
};
